import React from 'react';
import { connect } from 'react-redux';
import Head from 'next/head';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import classNames from 'classnames';
import get from 'lodash.get';

import { loadArticle } from 'redux/modules/article';
import { loadCard, fetchLatestCardsByTaxonomy } from 'redux/modules/liveBlog';
import { bulkUpdate as navUpdateAction } from 'redux/modules/navbar';
import { globalLoadComplete } from 'redux/modules/router';
import { setPageView, setVertical } from 'redux/modules/shared';

import { articleViewHandler, getDatePublished } from 'lib/blogUtils';
import { article as ArticlePropType } from 'lib/CustomPropTypes';
import { layout as servicesLayoutPropType } from 'lib/CustomPropTypes/services';
import getPathName from 'lib/getPathName';
import LegacyIDService from 'lib/LegacyIDService';
import { isBlogEventEnded, isBlogLive, shouldRenderRegularBlog } from 'lib/liveBlog';
import { navbar, NAVBAR_THEME } from 'lib/navbar';
import scrollTo from 'lib/scrollToElement';
import { extractTermPaths } from 'lib/taxonomy';

import AdsBundle from 'components/AdsBundle';
import { AnalyticsLaunchScripts } from 'components/AnalyticsLaunchScripts';
import AMPHTMLLink from 'components/AMP/HTMLLink';
import ArticleHero from 'components/Article/ArticleHero';
import ArticleBody from 'components/Article/ArticleBody';
import WindowTopObserver from 'components/IntObserver/WindowTopObserver';
import getLiveBlogMetaData from 'components/LiveBlog/getLiveBlogMetaData';
import { GlobalMetadata } from 'components/PageMetadata';
import LiveFlag from 'components/LiveFlag';
import ScrollingAnalytics from 'components/ScrollingAnalytics';
import { HeaderAndFooter } from 'components/services/HeaderAndFooter';
import IconfontStyleTag from 'components/IconfontStyleTag';
import UniversalCheckout from 'components/UniversalCheckout';

import 'assets/stylesheets/main.scss';
import 'assets/stylesheets/toolkit.scss';
import styles from './liveBlog.module.scss';
import globalContainerStyles from '../globalContainerStyles.module.scss';

import ErrorPage from '../_error';

const queryLimit = 11;
const block = 'liveBlog';
const pageView = 'article';

const navbarConfig = {
  theme: () => NAVBAR_THEME.VERTICAL,
  headline: ({ content }) => get(content, ['headline'], null),
  activeAt: 160,
  showShortcuts: ({ content }) => {
    const primaryTopic = get(content, 'taxonomy.primaryTopic');
    if (primaryTopic) {
      return !extractTermPaths(primaryTopic).includes('nbcnews/section/politics/2020-election');
    }
    return true;
  },
};

const mapStateToProps = ({
  article, liveBlog, shared, router,
}) => ({
  articles: article.content,
  activeItems: liveBlog.activeItems,
  card: liveBlog.card,
  isChromeless: shared.isChromeless,
  latestDate: liveBlog.latestDate,
  path: router.path,
});

class LiveBlogPage extends React.Component {
  static async getInitialProps(ctx) {
    const {
      req: {
        params: {
          cardID,
          id: rawID,
        },
        query: {
          page,
        },
      },
      res: {
        locals: {
          domain,
          hostname,
          services,
          vertical,
        },
      },
      asPath,
      store,
    } = ctx;

    const id = LegacyIDService.article(rawID);

    const initialPromiseArray = [
      store.dispatch(loadArticle(id)),
      store.dispatch(setPageView(pageView)),
      store.dispatch(setVertical(vertical)),
    ];
    if (cardID) {
      initialPromiseArray.push(
        store.dispatch(loadCard(cardID)),
      );
    }
    await Promise.all(initialPromiseArray);

    // Populate props with HFS component data
    const layout = {};
    if (services && services.layout && services.layout.getComponent) {
      layout.header = await services.layout.getComponent(vertical, 'header');
      layout.footer = await services.layout.getComponent(vertical, 'footer');
    }

    const {
      article: liveBlogAfterLoad,
      liveBlog: { card },
    } = store.getState();

    const content = get(liveBlogAfterLoad, ['content', 0], {});
    const body = get(content, 'body', []);
    const headline = get(content, 'headline', {});
    const primaryUrl = get(content, 'url.primary', '');
    const taxonomy = body.filter((el) => el.type === 'embeddedTaxonomy');
    const path = get(taxonomy, '[0].taxonomy.path', false);

    const statusCode = get(liveBlogAfterLoad, ['error', 'status'], 200);
    if (statusCode >= 400) {
      ctx.res.statusCode = statusCode;
    }

    const secondaryPromiseArray = [
      store.dispatch(navUpdateAction({
        shareUrl: getPathName(primaryUrl),
        headline,
      })),

      store.dispatch(globalLoadComplete({
        domain,
        host: hostname,
        navigating: false,
        path: asPath,
        transporting: false,
      })),
    ];

    if (path) {
      const params = {
        datePublished: getDatePublished(card),
        page,
        path,
        queryLimit,
      };
      // Load initial set of cards
      secondaryPromiseArray.push(
        store.dispatch(fetchLatestCardsByTaxonomy(params)),
      );
    }

    await Promise.all(secondaryPromiseArray);

    return {
      domain,
      host: hostname,
      layout,
      navigating: false,
      pageView,
      path: asPath,
      statusCode,
      vertical,
    };
  }

  static propTypes = {
    activeItems: PropTypes.arrayOf(
      PropTypes.shape({}),
    ),
    articles: PropTypes.arrayOf(ArticlePropType).isRequired,
    card: PropTypes.objectOf(PropTypes.any),
    isChromeless: PropTypes.bool,
    latestDate: PropTypes.string,
    layout: servicesLayoutPropType.isRequired,
    path: PropTypes.string,
    statusCode: PropTypes.number.isRequired,
    themesCssPath: PropTypes.string,
    vertical: PropTypes.string.isRequired,
  };

  static childContextTypes = {
    isLiveBlog: PropTypes.bool,
    setStickyVideoFlag: PropTypes.func,
  };

  static defaultProps = {
    activeItems: [],
    card: {},
    isChromeless: false,
    latestDate: null,
    path: '',
    themesCssPath: '',
  };

  constructor(props) {
    super(props);
    this.state = {
      hasStickyVideo: false,
    };
  }

  getChildContext = () => ({
    setStickyVideoFlag: this.setStickyVideoFlag,
    isLiveBlog: true,
  })

  getStylizedHeader = () => (
    <button
      type="button"
      className={classNames(styles.stylizedHeader, 'publico-hed red lh-title f7 f8-m f9-xl bw0 pa0 ma0 bg-transparent')}
      onClick={() => {
        let top = 500;
        let selector = document.querySelector('.LiveBlog');
        if (selector && selector.getBoundingClientRect) {
          top = selector.getBoundingClientRect().top - 200;
        } else {
          selector = document.querySelector('.articleBody [class*="lastSection"]');
          if (selector && selector.getBoundingClientRect) {
            ({ top } = selector.getBoundingClientRect());
          }
        }
        scrollTo(top, 500);
      }}
    >
      Live Blog&nbsp;/&nbsp;
    </button>
  )

  getStylizedTag = () => {
    const { latestDate } = this.props;

    // Need date value from an item to check
    if (!latestDate) {
      return null;
    }

    let isLive = false;
    let label = i18next.t('EVENT ENDED');
    const flag = get(this.props, 'articles[0].flag', null);

    // Editor has entered "event ended" in the article flag
    if (!isBlogEventEnded(flag)) {
      // Else use last card publish date to determine live
      isLive = isBlogLive(latestDate);
      if (isLive) {
        label = typeof flag === 'string' && flag.length ? flag : i18next.t('LIVE COVERAGE');
      }
    }

    return (
      <div className={styles.stylizedTag}>
        <LiveFlag isLive={isLive}>
          <span className={classNames(`f3 f4-l dib ${styles.liveFlagText}`, { tc: !isLive })}>{label}</span>
        </LiveFlag>
      </div>
    );
  }

  setStickyVideoFlag = (hasStickyVideo) => {
    this.setState({ hasStickyVideo });
  }

  render() {
    const {
      activeItems,
      articles,
      card,
      isChromeless,
      layout,
      path,
      statusCode,
      themesCssPath,
      vertical,
    } = this.props;

    if (parseInt(statusCode, 10) >= 400) {
      return (
        <ErrorPage
          statusCode={statusCode === 404 ? 404 : 500}
          vertical={vertical}
          layout={layout}
        />
      );
    }

    const article = articles && articles.length && articles[0];

    if (!article) {
      return null;
    }

    const { subType } = article;

    const isRegularBlog = shouldRenderRegularBlog(subType);

    const { hasStickyVideo } = this.state;

    if (card) {
      delete card.url;
      card.url = {
        primary: `${article.url.primary}/${card.id}`,
      };
    }

    const articleMetaData = { items: activeItems, ...article };
    const metaData = getLiveBlogMetaData(articleMetaData, card, vertical, isRegularBlog);
    const articleHeroHeadlineClasses = ['f8', 'f9-m', 'f9-l', 'f10-xl', 'fw3', 'mb3', 'mt0'];

    return (
      <>
        <Head>
          {themesCssPath && (
            <link href={themesCssPath} rel="stylesheet" key={themesCssPath} />
          )}
          <IconfontStyleTag />
        </Head>

        <div
          className={classNames(
            globalContainerStyles.container,
            'bg-knockout-primary',
          )}
          id="content"
        >
          <HeaderAndFooter layout={layout}>
            <WindowTopObserver
              threshold={0}
              callback={(event) => articleViewHandler(event, this.props)}
            >
              <ScrollingAnalytics contentType="liveBlog" contentUrl={article.url.primary}>
                <GlobalMetadata />
                {metaData}
                <AMPHTMLLink
                  content={article}
                  isLiveBlog
                  vertical={vertical}
                />
                <div
                  key={`liveBlogWrapper-${article.id}`}
                  className={classNames(
                    block,
                    styles.liveBlog,
                    {
                      [styles.hasStickyVideo]: hasStickyVideo,
                    },
                  )}
                >
                  <ArticleHero
                    content={article}
                    vertical={vertical}
                    headlineClassName={articleHeroHeadlineClasses}
                    stylizedHeader={!isRegularBlog && this.getStylizedHeader()}
                    stylizedTag={!isRegularBlog && this.getStylizedTag()}
                    isLiveBlog
                    path={path}
                  />
                  <ArticleBody
                    vertical={vertical}
                    article={article}
                    index={0}
                    path={path}
                    isChromeless={isChromeless}
                    shouldRenderRightRailTaboola={
                      ({ isFirstSection }) => isFirstSection && activeItems.length > 1
                    }
                    getRightRailAdConfig={({ isFirstSection }) => (
                      isFirstSection && activeItems.length > 1
                        ? { display: true, slot: 'boxflex' }
                        : { display: false }
                    )}
                  />
                </div>
              </ScrollingAnalytics>
            </WindowTopObserver>
          </HeaderAndFooter>
        </div>

        <AnalyticsLaunchScripts />
        <AdsBundle />
        <UniversalCheckout vertical={vertical} />
      </>
    );
  }
}

export default navbar(navbarConfig)(
  connect(mapStateToProps)(
    LiveBlogPage,
  ),
);
